import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { IActivityType } from '../locks-type';
import { Utility } from 'src/app/shared/utils/utility.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { TranslateService } from '@ngx-translate/core';

/**
 * Maps the building activity-type keys (see Utility.getBuildingActivityTypes) to
 * the underlying device event codes understood by the event-history backend.
 */
const BUILDING_EVENT_CODE_MAP: { [key: string]: string[] } = {
  locked: ['0xe1', '0x00', '0x51', '0x01'],
  unlocked: ['48', '61', '87', '60', '0xe2', '0x02', '0x52'],
};

@Component({
  selector: 'app-building-filter-actvity',
  templateUrl: './building-filter-actvity.component.html',
  styleUrls: ['./building-filter-actvity.component.scss'],
})
export class BuildingFilterActvityComponent implements OnInit {
  formGroup!: FormGroup;
  readonly CONSTANTS = CONSTANTS;
  readonly LANG_CONST = LANG_CONST;
  orignalFormData: string | undefined;
  subscription: Subscription | undefined;
  stepper = 1;
  filterActivity!: any;
  activityTypes: IActivityType[] = [];

  /**
   * @description constructor injects all necessary services and restores the
   *              previously applied filter passed via the dialog data
   */
  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<BuildingFilterActvityComponent>,
    private localizationService: LocalizationService,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) modalData: any
  ) {
    this.filterActivity = {
      userType: modalData?.userType ?? null,
      dates: modalData?.dates!,
      lockType:
        modalData?.lockType && modalData.lockType.length > 0
          ? [...modalData.lockType]
          : [],
      lockIds:
        modalData?.lockIds && modalData.lockIds.length > 0
          ? [...modalData.lockIds]
          : [],
      entityType: CONSTANTS.BUILDING,
    };
  }

  /**
   * @description build the reactive form and restore selections on init
   */
  ngOnInit(): void {
    this.localizationService.setupLocalization();
    this.activityTypes = [...Utility.getBuildingActivityTypes(this.translate)];
    this.createControls();
  }

  /**
   * @description create the form groups consumed by the child screens
   */
  createControls(): void {
    this.formGroup = this.fb.group({
      userType: this.fb.group({
        userType: [null],
      }),
      dateRange: this.fb.group({
        dateTodateFrom: [''],
      }),
      locks: this.fb.group({
        eventType: [[]],
      }),
      lockIds: this.fb.group({
        eventTypeIds: [[]],
      }),
    });

    if (this.filterActivity.userType) {
      this.formGroup.controls.userType.patchValue({
        userType: this.filterActivity.userType,
      });
    }

    if (this.filterActivity.dates) {
      this.formGroup.controls.dateRange.patchValue({
        dateTodateFrom: this.filterActivity.dates,
      });
    }

    if (this.filterActivity.lockType) {
      this.formGroup.controls.locks.patchValue({
        eventType: this.filterActivity.lockType,
      });
    }

    if (this.filterActivity.lockIds) {
      this.formGroup.controls.lockIds.patchValue({
        eventTypeIds: this.filterActivity.lockIds,
      });
      this.activityTypes = this.activityTypes?.map(
        (activityType: IActivityType) => ({
          ...activityType,
          active:
            this.filterActivity.lockIds?.includes(activityType.eventCode) ||
            false,
        })
      );
    }

    this.orignalFormData = JSON.stringify(this.formGroup.value);
  }

  /**
   * @description gather the selected filters, expand the activity-type keys to
   *              device event codes and close the dialog with the result
   */
  submitToParent(): void {
    const lockIds: string[] =
      this.formGroup.controls?.lockIds?.value?.eventTypeIds || [];
    const eventCodes = lockIds.reduce<string[]>(
      (codes: string[], key: string) =>
        codes.concat(BUILDING_EVENT_CODE_MAP[key] || []),
      []
    );

    this.filterActivity = {
      userType: this.formGroup.controls?.userType?.value?.userType ?? null,
      dates: this.formGroup.controls?.dateRange?.value?.dateTodateFrom,
      lockType: this.formGroup.controls?.locks?.value?.eventType,
      lockIds: lockIds,
      eventCodes: eventCodes,
      cursor: false,
      prepend: false,
    };
    this.dialogRef.close(this.filterActivity);
  }

  /**
   * @description reset all child forms and states
   * @returns void
   */
  formReset(): void {
    this.formGroup.reset();
    this.stepper = 1;
  }

  /**
   * @description close the dialog without applying any change
   * @returns void
   */
  closeDialog(): void {
    this.dialogRef?.close(undefined);
  }

  /**
   * @description reset the active state of activity types
   * @returns void
   */
  resetFilterData(): void {
    this.activityTypes = this.activityTypes?.map(
      (activityType: IActivityType) => {
        if (activityType.active === true) {
          activityType.active = false;
        }
        return activityType;
      }
    );
  }
}
